import {
  baseApi,
  createLiveChannel,
  createLiveHandlerRegistry,
  liveEventReceived,
  liveStatusChanged,
  maintenanceApi,
  sessionApi,
  MAINTENANCE_INVALIDATION,
  MAINTENANCE_WORKPLACES_INVALIDATION,
  SERVICE_MAINTENANCE_CHANGED,
  SERVICE_MAINTENANCE_WORKPLACES_CHANGED,
  type LiveChannel,
  type LiveSocket,
} from '@krp/client-runtime'

import type { AppDispatch } from '../app/types'
import { MODULES } from './modules'

/** Как открыть сокет и куда: браузер (`main.tsx`) или тест. */
export interface LiveTransport {
  url: () => string
  connect: (url: string) => LiveSocket
}

/** Канал без транспорта: не подключается никогда и ничего не шлёт (тесты, которым живой канал не нужен). */
export const DISABLED_LIVE_CHANNEL: LiveChannel = {
  start: () => {},
  stop: () => {},
  status: 'off',
}

/** Раз в сколько неудачных подключений (помимо первой) спрашивать «кто я»: сервер входа не долбим. */
const SESSION_CHECK_EVERY_N_FAILURES = 3

/**
 * Реестр обработчиков событий — из модулей пакетов (G3): каждый пакет
 * объявляет свои в `UiModule.live`, приложение их не перечисляет.
 */
export const LIVE_HANDLERS = createLiveHandlerRegistry(MODULES.flatMap((module) => module.live ?? []))

/**
 * Живой канал вкладки: события → перечитать теги (RTK Query), обрыв →
 * переподключение, восстановление → перечитать то, что могло измениться за
 * обрыв. Запуск и остановку по сессии делает listener (`listeners.ts`).
 */
export function createAppLiveChannel(transport: LiveTransport, dispatch: AppDispatch): LiveChannel {
  return createLiveChannel({
    url: transport.url,
    connect: transport.connect,
    onEnvelope: (envelope) => {
      const tags = LIVE_HANDLERS.invalidates(envelope)
      if (tags.length > 0) dispatch(baseApi.util.invalidateTags(tags))
      // RP-MNT: общесистемное событие режима обновления идёт без привязки к ресурсу, мимо реестра обработчиков пакетов — перечитать состояние.
      if (envelope.type === SERVICE_MAINTENANCE_CHANGED) dispatch(maintenanceApi.util.invalidateTags([...MAINTENANCE_INVALIDATION]))
      // Пульт обновления: касса открылась, закрылась или сменила состояние — перечитать список.
      if (envelope.type === SERVICE_MAINTENANCE_WORKPLACES_CHANGED) dispatch(maintenanceApi.util.invalidateTags([...MAINTENANCE_WORKPLACES_INVALIDATION]))
      dispatch(liveEventReceived(envelope))
    },
    onStatus: (status) => dispatch(liveStatusChanged(status)),
    onResync: () => {
      const types = LIVE_HANDLERS.resync()
      if (types.length > 0) dispatch(baseApi.util.invalidateTags(types))
      // Связь вернулась (в т. ч. после перезапуска на обновлении): перечитать режим — он мог смениться за обрыв.
      dispatch(maintenanceApi.util.invalidateTags([...MAINTENANCE_INVALIDATION, ...MAINTENANCE_WORKPLACES_INVALIDATION]))
    },
    onConnectFailed: (attempt) => {
      // Отказ upgrade (401 после выхода или отзыва сессии) браузер не отличает от сети. «Кто я» скажет: 401 —
      // сессия кончилась, listener остановит канал. Не на каждую попытку — сервер входа не долбим.
      if (attempt === 1 || attempt % SESSION_CHECK_EVERY_N_FAILURES === 0) {
        void dispatch(sessionApi.endpoints.sessionMe.initiate(undefined, { forceRefetch: true, subscribe: false }))
      }
    },
  })
}
